import { CheckboxRow } from "@/components/molecules/CheckboxRow/CheckboxRow";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  metalIcon,
  pastAndPresentIcon,
  rarityIcon,
} from "../../../../../designSystemIcons";
import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import { loadedContentSelector } from "../../../../../redux/loadedContent/loadedContentSlice";
import { MultiSelectDropdown } from "../../../../atoms/Dropdown/MultiSelectDropdown";
import { DropdownItem } from "../../../../atoms/Dropdown/MultiSelectDropdownItems";
import { ComponentContainer } from "../../components/ComponentContainer/ComponentContainer";
import { PanelContainer } from "../../components/PanelContainer/PanelContainer";
import { PanelHeader } from "../PanelHeader/PanelHeader";

type Props = {
  selectedPnPs: number[];
  selectedVersions: number[];
  selectedMetals: number[];
  excludeIconsHeroes: boolean;
  handleClickedClose: () => void;
  handleSelectedPnP: (id: number) => void;
  handleSelectedVersion: (id: number) => void;
  handleExcludeIconsHeroesUpdated: (excludeIconsHeroes: boolean) => void;
  handleSelectedMetal: (id: number) => void;
  includeLoanPlayers: boolean;
  handleIncludeLoanPlayersUpdated: (includeLoanPlayers: boolean) => void;
};

const metalOptions: DropdownItem[] = [
  {
    id: 0,
    text: "Gold",
    icon: (
      <img
        alt="Gold"
        className="h-5 mx-auto"
        src={`${
          import.meta.env.VITE_APP_ASSETS_BASE_URL
        }/fut23/cards/e_1_3.png?scale=50.png`}
      />
    ),
  },
  {
    id: 1,
    text: "Silver",
    icon: (
      <img
        alt="Silver"
        className="h-5 mx-auto"
        src={`${
          import.meta.env.VITE_APP_ASSETS_BASE_URL
        }/fut23/cards/e_1_2.png?scale=50.png`}
      />
    ),
  },
  {
    id: 2,
    text: "Bronze",
    icon: (
      <img
        alt="Bronze"
        className="h-5 mx-auto"
        src={`${
          import.meta.env.VITE_APP_ASSETS_BASE_URL
        }/fut23/cards/e_1_1.png?scale=50.png`}
      />
    ),
  },
];

export const MoreFiltersMode: React.FC<Props> = ({
  selectedPnPs,
  selectedVersions,
  selectedMetals,
  excludeIconsHeroes,
  includeLoanPlayers,
  handleClickedClose,
  handleSelectedPnP,
  handleSelectedVersion,
  handleExcludeIconsHeroesUpdated,
  handleIncludeLoanPlayersUpdated,
  handleSelectedMetal,
}) => {
  const { clubs: loadedClubs, versions: loadedVersions } = useSelector(
    loadedContentSelector,
  );
  const ref = useOutsideClick(handleClickedClose);
  const [pnps, setPnps] = useState<DropdownItem[]>([]);
  const [versions, setVersions] = useState<DropdownItem[]>([]);
  const [metals, setMetals] = useState<DropdownItem[]>([]);
  const [pnpSearch, setPnpSearch] = useState("");
  const [versionsSearch, setVersionsSearch] = useState("");

  useEffect(() => {
    if (pnps.length === 0) {
      setPnps(
        loadedClubs.map((club) => ({
          id: club.id,
          text: club.name + (club.isWomen ? " (w)" : ""),
          icon: (
            <img
              className="w-5 h-5 my-auto"
              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/clubs/${
                club.id
              }.png?scale=50`}
              alt={club.name}
            />
          ),
        })),
      );
    }
    if (versions.length === 0) {
      setVersions(
        loadedVersions.map((version) => ({
          id: version.id,
          text: version.name,
          icon: (
            <img
              className="w-5 h-5 my-auto"
              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/cards/e_${
                version.id
              }_${version.hasLevels ? 3 : 0}.png`}
              alt={version.name}
            />
          ),
        })),
      );
    }
    if (metals.length === 0) {
      setMetals(metalOptions);
    }
  }, [loadedClubs, loadedVersions, versions, pnps, metals]);

  return (
    <div className="h-screen mobile:h-auto" ref={ref}>
      <PanelHeader
        title={"More General Filters"}
        onClose={handleClickedClose}
      />
      <PanelContainer>
        <ComponentContainer marginBottom="mb-[350px] mt-8">
          <div className="flex flex-col gap-y-1">
            <MultiSelectDropdown
              items={
                versionsSearch
                  ? [
                      ...versions.filter((l) =>
                        l.text
                          .toLowerCase()
                          .includes(versionsSearch.toLowerCase()),
                      ),
                    ]
                  : versions
              }
              onSelect={handleSelectedVersion}
              placeHolder={"Rarity"}
              selectedIds={selectedVersions}
              onClear={() => handleSelectedVersion(-1)}
              placeHolderIcon={rarityIcon}
              searchValue={versionsSearch}
              onSearchChange={setVersionsSearch}
              setDropdownItems={setVersions}
            />
            <MultiSelectDropdown
              items={metals}
              onSelect={handleSelectedMetal}
              placeHolder={"Metal"}
              selectedIds={selectedMetals}
              onClear={() => handleSelectedMetal(-1)}
              placeHolderIcon={metalIcon}
              setDropdownItems={setMetals}
            />
            <MultiSelectDropdown
              items={
                pnpSearch
                  ? [
                      ...pnps.filter((c) =>
                        c.text.toLowerCase().includes(pnpSearch.toLowerCase()),
                      ),
                    ]
                  : pnps
              }
              onSelect={handleSelectedPnP}
              placeHolder={"Past & Present"}
              selectedIds={selectedPnPs}
              onClear={() => handleSelectedPnP(-1)}
              placeHolderIcon={pastAndPresentIcon}
              searchValue={pnpSearch}
              onSearchChange={setPnpSearch}
              setDropdownItems={setPnps}
            />
            <div className="flex flex-col mt-2 text-xs gap-y-2">
              <CheckboxRow
                label={"Exclude Icons & Heroes"}
                fullWidth
                checked={excludeIconsHeroes}
                onClick={() =>
                  handleExcludeIconsHeroesUpdated(!excludeIconsHeroes)
                }
              />
              <CheckboxRow
                label={"Include Loan Players"}
                fullWidth
                checked={includeLoanPlayers}
                onClick={() =>
                  handleIncludeLoanPlayersUpdated(!includeLoanPlayers)
                }
              />
            </div>
          </div>
        </ComponentContainer>
      </PanelContainer>
    </div>
  );
};
